import React, {FC} from "react"
import {formatCurrency} from "@irati/utils/currency/formatCurrency"
import styled, {css} from "styled-components"
import {Body} from "@irati/ui/src/Typography"
import Card from "@irati/ui/src/Layout/Card"
import Flex from "@irati/ui/src/Flex"
import {FormattedMessage} from "@irati/utils/lang"
import {useTheme} from "@irati/ui/src/global/useTheme"
import {TiersResponse} from "@irati/types"
import {h6} from "@irati/ui/src/Typography/Heading/styles"

interface Current {
  isCurrent?: boolean
}

export const Footer = styled(Body)<Current>`
  ${h6}
  padding: ${({theme}) => theme.spacing.half} 0;

  ${({isCurrent, theme}) =>
    isCurrent
      ? css`
          background-color: ${theme.colors.primary2};
          color: ${theme.colors.greys.white};
        `
      : css`
          background-color: ${theme.colors.secondary1};
          color: ${theme.colors.greys.black};
        `}
`

const StyledCard = styled(Card)<Current>`
  ${({isCurrent, theme}) => isCurrent && `outline: 2px solid ${theme.colors.primary2};`}
  overflow: hidden;
  border-radius: ${({theme}) => theme.border.radius.medium};
`

type Props = TiersResponse & {
  isFirst?: boolean
  isLast?: boolean
  isCurrent?: boolean
  purchaseCurrency: string
}

const ServiceFeeCard: FC<Props> = (props: Props) => {
  const {
    endValue,
    baseServiceFee,
    baseServiceFeeRate = 0,
    tierServiceFeeRate = 0,
    tierServiceFee,
    purchaseCurrency,
    startValue,
    isCurrent = false
  } = props
  const theme = useTheme()
  const discounted = tierServiceFeeRate < baseServiceFeeRate
  const footerText = getFooterText(props)

  return (
    <StyledCard flush isCurrent={isCurrent}>
      <Flex direction="column" justifyContent="flex-end" height="112px">
        <Flex direction="column" justifyContent="center" alignItems="center" flex={"1"}>
          <Flex marginBottom>
            <Flex gap={0.5}>
              <Body textDecoration={discounted ? "line-through" : ""} color={theme.colors.greys.black}>
                {discounted ? baseServiceFee : tierServiceFee}%
              </Body>
              {discounted && <Body color={theme.colors.tertiary.g_600}>{tierServiceFee}%</Body>}
            </Flex>
          </Flex>
          {endValue === -1 ? (
            <Body>{formatCurrency({currencyCode: purchaseCurrency, value: startValue})} +</Body>
          ) : (
            <Body noWrap>
              {formatCurrency({currencyCode: purchaseCurrency, value: startValue})} &nbsp;-&nbsp;
              {formatCurrency({currencyCode: purchaseCurrency, value: endValue})}
            </Body>
          )}
        </Flex>

        {footerText ? (
          <Footer textAlign="center" isCurrent={isCurrent}>
            <FormattedMessage {...footerText} />
          </Footer>
        ) : null}
      </Flex>
    </StyledCard>
  )
}

interface FormatTextProps {
  id: string
  values?: Record<string, string>
}

function getFooterText({
  purchaseCurrency,
  endValue,
  baseServiceFeeRate = 0,
  tierServiceFeeRate = 0,
  isCurrent = false,
  isFirst = false,
  isLast = false
}: Props): FormatTextProps | null {
  const maxSaveTierFee = isLast ? 0 : endValue * (baseServiceFeeRate - tierServiceFeeRate)

  if (isCurrent) {
    return {id: "wallet.reward.admin.card.topup.serviceFeeTier.current"}
  } else if (isFirst) {
    return null
  } else if (isLast) {
    return {id: "wallet.reward.admin.card.topup.serviceFeeTier.bestValue"}
  } else if (maxSaveTierFee) {
    return {
      id: "wallet.reward.admin.card.topup.serviceFeeTier.saveUpTo",
      values: {value: formatCurrency({value: maxSaveTierFee, currencyCode: purchaseCurrency})}
    }
  }

  return null
}

export default ServiceFeeCard
